import { useTranslations } from '@vocab/react';
import {
  Card,
  Columns,
  Column,
  Stack,
  Text,
  ButtonIcon,
  IconEdit,
  Drawer,
  useToast,
  Box,
} from 'braid-design-system';
import { useEffect, useState } from 'react';

import SendCodeFormContainer from 'src/components/Account/ChangeEmail/SendCodeForm/FormContainer';
import VerifyCodeFormContainer from 'src/components/Account/ChangeEmail/VerifyCodeForm/FormContainer';
import { LoadingItem } from 'src/components/LoadingItem/LoadingItem';
import { PageTitles } from 'src/components/Notifications/pageTitle';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { useTypedSelector } from 'src/reducers';

import translations from './.vocab';

export const ChangeEmail = () => {
  const { t } = useTranslations(translations);
  const { trackAccountLink } = useAnalytics();

  const email = useTypedSelector((state) => state.user.emailAddress);
  const brand = useTypedSelector((state) => state.page.pageAnalytics.brand);

  const [openingEventTriggered, setOpeningEventTriggered] = useState(false);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [sendCodeShown, setSendCodeShown] = useState(true);
  const [success, setSuccess] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    if (openingEventTriggered) {
      trackAccountLink({
        eventName: 'settings_section_displayed',
        currentPageSection: 'change email',
        passwordlessAuthentication: 'full',
        authenticationFlow: 'changeEmail',
      });
    }
  }, [openingEventTriggered, trackAccountLink]);

  const toggle = () => {
    const wasClosed = drawerOpened === false;
    if (wasClosed) {
      setOpeningEventTriggered(true);
      setSuccess(false);
      setSendCodeShown(true);
    }
    setDrawerOpened((currentState) => !currentState);
  };

  const showToast = useToast();
  useEffect(() => {
    if (success === true) {
      showToast({
        key: 'changeEmailSuccessToast',
        tone: 'positive',
        message: t('Done! Your email has been updated.'),
      });
    }
  }, [showToast, success, t]);

  return (
    <Box data-testid="change-email-card">
      <Card>
        <Columns space="small">
          <Column>
            <Stack space="medium">
              <Text weight="strong">{t('Email')}</Text>
              {Boolean(email) ? (
                <Text tone="secondary">{email}</Text>
              ) : (
                <LoadingItem size="standard" width="small" />
              )}
            </Stack>
          </Column>
          <Column width="content">
            <ButtonIcon
              id="changeEmailButton"
              variant="transparent"
              icon={<IconEdit />}
              label={t('Change your email')}
              onClick={toggle}
            />
          </Column>
        </Columns>
      </Card>
      <Drawer
        id="changeEmailDrawer"
        title={t('Change your email')}
        open={drawerOpened === true}
        onClose={toggle}
        width="small"
      >
        <Text>
          {t(
            'Email is used to sign in to BRAND and to be contacted by employers.',
            {
              brand: PageTitles[brand],
            },
          )}
        </Text>

        {sendCodeShown ? (
          <SendCodeFormContainer
            toggle={toggle}
            setNewEmail={setNewEmail}
            setSendCodeShown={setSendCodeShown}
            setSuccess={setSuccess}
          />
        ) : (
          <VerifyCodeFormContainer
            toggle={toggle}
            newEmail={newEmail}
            setSuccess={setSuccess}
          />
        )}
      </Drawer>
    </Box>
  );
};
